/**
 * Custom Cut Selector Web Component
 * Handles length input, price calculation, and form submission for products sold by the metre
 */
class CustomCutSelector extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      lengthInput: '.custom-cut-selector__input',
      feedDirectionInput: '.custom-cut-selector__feed-direction-input',
      quantityInput: '.custom-cut-selector__qty-input',
      quantityButtons: '.custom-cut-selector__qty-btn',
      quantityDisplay: '.custom-cut-selector__qty-display',
      lengthDisplay: '.custom-cut-selector__length-display',
      feedDirectionDisplay: '.custom-cut-selector__feed-direction-display',
      metresCount: '.custom-cut-selector__metres-count',
      cutsCount: '.custom-cut-selector__cuts-count',
      totalAmount: '.custom-cut-selector__total-amount',
      unitPrice: '.custom-cut-selector__unit-price',
      exactLengthHidden: '.custom-cut-selector__exact-length',
      multiplierHidden: '.custom-cut-selector__multiplier',
      shopifyQtyHidden: '.custom-cut-selector__shopify-qty',
    };

    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.updateCalculations();
  }

  disconnectedCallback() {
    this.abortController.abort();
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get formId() {
    return this.dataset.formId;
  }

  get variantPrice() {
    return parseFloat(this.dataset.variantPrice) / 100; // Shopify prices are in cents
  }

  get minLength() {
    return parseInt(this.dataset.minLength) || 100;
  }

  get maxLength() {
    return parseInt(this.dataset.maxLength) || 20000;
  }

  get form() {
    return document.getElementById(this.formId);
  }

  cacheElements() {
    this.lengthInput = this.querySelector(this.selectors.lengthInput);
    this.feedDirectionInput = this.querySelector(this.selectors.feedDirectionInput);
    this.quantityInput = this.querySelector(this.selectors.quantityInput);
    this.quantityDisplayEl = this.querySelector(this.selectors.quantityDisplay);
    this.lengthDisplayEl = this.querySelector(this.selectors.lengthDisplay);
    this.feedDirectionDisplayEl = this.querySelector(this.selectors.feedDirectionDisplay);
    this.metresCountEl = this.querySelector(this.selectors.metresCount);
    this.cutsCountEl = this.querySelector(this.selectors.cutsCount);
    this.totalAmountEl = this.querySelector(this.selectors.totalAmount);
    this.unitPriceEl = this.querySelector(this.selectors.unitPrice);
    this.exactLengthHidden = this.querySelector(this.selectors.exactLengthHidden);
    this.multiplierHidden = this.querySelector(this.selectors.multiplierHidden);
    this.shopifyQtyHidden = this.querySelector(this.selectors.shopifyQtyHidden);
  }

  bindEvents() {
    const signal = this.abortController.signal;

    // Length input changes
    this.lengthInput?.addEventListener('input', this.handleLengthChange.bind(this), { signal });
    this.lengthInput?.addEventListener('blur', this.validateLength.bind(this), { signal });
    
    // Feed Direction input changes
    this.feedDirectionInput?.addEventListener('change', this.handleFeedDirectionChange.bind(this), { signal });

    // Quantity input changes
    this.quantityInput?.addEventListener('input', this.handleQuantityChange.bind(this), { signal });
    this.quantityInput?.addEventListener('change', this.handleQuantityChange.bind(this), { signal });

    // Quantity buttons (custom class to avoid theme conflicts)
    this.querySelectorAll(this.selectors.quantityButtons).forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityButtonClick.bind(this), { signal });
    });

    // Form submission - intercept to update quantity
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this), { signal });
    }

    // Listen for variant changes via FoxTheme pubsub (has correct new variant data)
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
    }
  }

  handleLengthChange() {
    this.updateCalculations();
  }

  handleFeedDirectionChange() {
    this.feedDirectionDisplayEl.textContent = " with " + this.feedDirectionInput.value + " tail"
  }

  handleQuantityChange() {
    this.updateCalculations();
  }

  handleQuantityButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const action = button.dataset.action;
    let currentQty = parseInt(this.quantityInput.value) || 1;

    if (action === 'plus') {
      currentQty++;
    } else if (action === 'minus' && currentQty > 1) {
      currentQty--;
    }

    this.quantityInput.value = currentQty;
    this.updateCalculations();
  }

  validateLength() {
    let length = parseInt(this.lengthInput.value) || this.minLength;

    if (length < this.minLength) {
      length = this.minLength;
    } else if (length > this.maxLength) {
      length = this.maxLength;
    }

    this.lengthInput.value = length;
    this.updateCalculations();
  }

  handleVariantChange({ data }) {
    // Update variant price if variant changes (data comes from FoxTheme.pubsub)
    const { variant } = data || {};
    if (variant && variant.price) {
      this.dataset.variantPrice = variant.price;
      this.dataset.variantId = variant.id;
      
      // Update the unit price display
      if (this.unitPriceEl) {
        this.unitPriceEl.textContent = this.formatMoney(variant.price / 100) + '/m';
      }
      
      this.updateCalculations();
    }
  }

  handleFormSubmit(event) {
    // Ensure calculations are up to date before submission
    this.updateCalculations();
  }

  /**
   * Calculate billable metres (rounded up to nearest metre)
   * @param {number} lengthMm - Length in millimetres
   * @returns {number} - Number of metres to bill
   */
  calculateBillableMetres(lengthMm) {
    return Math.ceil(lengthMm / 1000);
  }

  /**
   * Update all calculations and display values
   */
  updateCalculations() {
    const lengthMm = parseInt(this.lengthInput?.value) || 1000;
    const cuts = parseInt(this.quantityInput?.value) || 1;

    // Calculate metres per cut and total
    const metresPerCut = this.calculateBillableMetres(lengthMm);
    const totalMetres = metresPerCut * cuts;
    const totalPrice = totalMetres * this.variantPrice;

    // Update display elements
    if (this.quantityDisplayEl) {
      this.quantityDisplayEl.textContent = cuts+"x";
    }

    if (this.lengthDisplayEl) {
      this.lengthDisplayEl.textContent = lengthMm;
    }

    if (this.metresCountEl) {
      this.metresCountEl.textContent = totalMetres;
    }

    if (this.cutsCountEl) {
      this.cutsCountEl.textContent = cuts;
    }

    if (this.totalAmountEl) {
      this.totalAmountEl.textContent = this.formatMoney(totalPrice);
    }

    // Update hidden form fields
    if (this.exactLengthHidden) {
      this.exactLengthHidden.value = lengthMm;
    }

    if (this.multiplierHidden) {
      this.multiplierHidden.value = metresPerCut;
    }

    // Critical: Update the actual quantity that goes to Shopify
    if (this.shopifyQtyHidden) {
      this.shopifyQtyHidden.value = totalMetres;
    }

    // Update the visible Cut length property value
    if (this.lengthInput) {
      this.lengthInput.value = lengthMm;
    }
  }

  /**
   * Format money value to locale string
   * @param {number} amount - Amount in currency units
   * @returns {string} - Formatted currency string
   */
  formatMoney(amount) {
    // Use Shopify's money format if available
    if (typeof FoxTheme !== 'undefined' && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(amount * 100); // Convert back to cents
    }

    // Fallback to basic formatting
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: Shopify?.currency?.active || 'AUD',
    }).format(amount);
  }
}

customElements.define('custom-cut-selector', CustomCutSelector);
